import React, { Suspense, useCallback, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import AppsIcon from '@atlaskit/icon/core/apps';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import type {
	RovoSettingsModalOpenOptions,
	RovoSettingsModalProps,
	RovoSettingsModalTriggerProps,
} from './types';

const triggerStyles = cssMap({
	button: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		backgroundColor: 'transparent',
		borderStyle: 'none',
		cursor: 'pointer',
		width: '100%',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	text: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
});

const DefaultTrigger = ({ open }: RovoSettingsModalTriggerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Pressable xcss={triggerStyles.button} onClick={() => open()}>
			<Flex alignItems="center" gap="space.075">
				<AppsIcon label="" color={token('color.icon.subtle')} />
				<Box xcss={triggerStyles.text}>{formatMessage(messages.defaultTriggerLabel)}</Box>
			</Flex>
		</Pressable>
	);
};

const LazyRovoSettingsModalProvider = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_rovo-settings-modal-provider" */ './provider'
	).then((mod) => ({
		default: mod.RovoSettingsModalLazy,
	})),
);

/**
 * Self-contained entrypoint for the Rovo Settings modal.
 *
 * The trigger renders eagerly. The provider tree and modal
 * are lazy-loaded only when the user first clicks the trigger.
 * Subsequent opens reuse the mounted provider — no re-mount or re-fetch.
 */
export const RovoSettingsModal = ({
	children: renderTrigger,
	...providerProps
}: RovoSettingsModalProps): React.JSX.Element => {
	const [isActivated, setIsActivated] = useState(false);
	const pendingOpenOptions = useRef<RovoSettingsModalOpenOptions | undefined>(undefined);
	const openModalRef = useRef<((options?: RovoSettingsModalOpenOptions) => void) | null>(null);

	const open = useCallback((options?: RovoSettingsModalOpenOptions) => {
		if (openModalRef.current) {
			// Provider already mounted — open directly
			openModalRef.current(options);
		} else {
			// First click — store options and mount the provider
			pendingOpenOptions.current = options;
			setIsActivated(true);
		}
	}, []);

	// Calling the render function directly (rather than mounting it as a
	// component) makes React diff the returned JSX in-place against the previous
	// render, so the trigger element survives parent re-renders even if the
	// render function's identity changes (e.g. inline arrows).
	const triggerNode = renderTrigger ? renderTrigger({ open }) : <DefaultTrigger open={open} />;

	return (
		<ErrorBoundary fallbackRender={() => null}>
			{triggerNode}
			{isActivated && (
				<Suspense fallback={null}>
					<LazyRovoSettingsModalProvider
						{...providerProps}
						pendingOpenOptions={pendingOpenOptions}
						openModalRef={openModalRef}
					/>
				</Suspense>
			)}
		</ErrorBoundary>
	);
};
